import React, { useMemo, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Loader2, LogIn, Sparkles, ShieldCheck } from 'lucide-react';
import CompanyLogo from '@/components/common/CompanyLogo';
import { authApi } from '../../lib/api/auth';
import { projectAnalysisProfileApi } from '@/lib/api/profile';
import { LoginFooterSvg } from './LoginFooterSvg';
import { LoginBrandFooter, LoginIconFloats } from './LoginPageChrome';
import { isPasswordTwoFactorRequired } from '../../lib/auth/passwordTwoFactor';

const Login: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [secondStepOpen, setSecondStepOpen] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);
  const [otpError, setOtpError] = useState('');
  const [pendingMobile, setPendingMobile] = useState('');
  const [cooldownLeft, setCooldownLeft] = useState(0);
  const [codeDigits, setCodeDigits] = useState<string[]>(Array(5).fill(''));
  const inputRefs = useRef<Array<HTMLInputElement | null>>([]);
  const reason = new URLSearchParams(location.search).get('expired');

  const from = (location.state as { from?: string })?.from || '/';
  const code = useMemo(() => codeDigits.join(''), [codeDigits]);

  React.useEffect(() => {
    if (cooldownLeft <= 0) return;
    const id = window.setInterval(() => setCooldownLeft((s) => Math.max(0, s - 1)), 1000);
    return () => window.clearInterval(id);
  }, [cooldownLeft]);

  const requestSecondStepOtp = async (mobileRaw?: string) => {
    const mobile = mobileRaw ?? pendingMobile;
    if (!mobile) return;
    setOtpLoading(true);
    setOtpError('');
    try {
      await authApi.requestLoginOtp({ mobile_national: mobile });
      setCooldownLeft(60);
      setSecondStepOpen(true);
      setTimeout(() => inputRefs.current[0]?.focus(), 50);
    } catch (e) {
      setOtpError(e instanceof Error ? e.message : 'ارسال کد ناموفق بود.');
    } finally {
      setOtpLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const res = await authApi.login({ username, password, remember });
      const user = res.user;
      const needsPassword2fa = isPasswordTwoFactorRequired(user.id);
      if (needsPassword2fa) {
        let mobileNational = '';
        try {
          const profile = await projectAnalysisProfileApi.fetch();
          mobileNational = (profile.mobile_national ?? '').replace(/\D/g, '').slice(0, 10);
          if (!/^9\d{9}$/.test(mobileNational) && profile.mobile?.startsWith('09')) {
            mobileNational = profile.mobile.slice(1);
          }
        } catch {
          mobileNational = (user.mobile ?? '').replace(/\D/g, '').slice(0, 10);
          if (mobileNational.length === 11 && mobileNational.startsWith('0')) {
            mobileNational = mobileNational.slice(1);
          }
        }
        if (!/^9\d{9}$/.test(mobileNational)) {
          setError('برای تایید دو مرحله‌ای، شماره موبایل در پروفایل کاربر ثبت نشده است.');
          return;
        }
        setPendingMobile(mobileNational);
        setCodeDigits(Array(5).fill(''));
        await requestSecondStepOtp(mobileNational);
        return;
      }
      navigate(from, { replace: true });
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'خطا در ورود. لطفاً دوباره تلاش کنید.');
    } finally {
      setLoading(false);
    }
  };

  const onDigitChange = (idx: number, value: string) => {
    const d = value.replace(/\D/g, '');
    if (!d) {
      setCodeDigits((prev) => {
        const next = [...prev];
        next[idx] = '';
        return next;
      });
      return;
    }
    const digit = d[d.length - 1];
    setCodeDigits((prev) => {
      const next = [...prev];
      next[idx] = digit;
      return next;
    });
    if (idx < 4) inputRefs.current[idx + 1]?.focus();
  };

  const verifySecondStep = async () => {
    setOtpLoading(true);
    setOtpError('');
    try {
      await authApi.verifyLoginOtp({ mobile_national: pendingMobile, code });
      navigate(from, { replace: true });
    } catch (e) {
      setOtpError(e instanceof Error ? e.message : 'کد نامعتبر است.');
    } finally {
      setOtpLoading(false);
    }
  };

  return (
    <div
      className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-slate-100 via-emerald-50/95 to-green-100/85 px-3 pb-28 pt-6 sm:px-4 sm:pb-32 sm:pt-8"
      dir="rtl"
    >
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.38]"
        style={{
          backgroundImage: `
            linear-gradient(to right, rgb(10 77 41 / 0.055) 1px, transparent 1px),
            linear-gradient(to bottom, rgb(10 77 41 / 0.055) 1px, transparent 1px)
          `,
          backgroundSize: '32px 32px',
        }}
      />

      <LoginIconFloats />
      <LoginFooterSvg />
      <LoginBrandFooter />

      <div className="relative z-10 w-full max-w-md animate-card-rise">
        <div className="relative rounded-2xl border border-emerald-200/70 bg-white/92 p-6 sm:p-8 shadow-2xl shadow-emerald-950/10 backdrop-blur-sm ring-1 ring-emerald-100/90">
          <div
            className="pointer-events-none absolute -inset-px rounded-2xl opacity-55"
            style={{
              background:
                'linear-gradient(135deg, rgba(16, 120, 62, 0.1) 0%, transparent 42%, rgba(10, 77, 41, 0.05) 100%)',
            }}
          />

          <div className="relative text-center mb-8">
            <CompanyLogo
              className="inline-flex h-[4.5rem] w-[4.5rem] items-center justify-center rounded-2xl bg-white p-1.5 shadow-lg ring-1 ring-emerald-200/80 animate-icon-glow-green"
              imgClassName="h-full w-full object-contain"
            />
            <h1 className="mt-5 text-2xl font-bold tracking-tight text-slate-800">آنالیز بها</h1>
            <p className="mt-1.5 text-sm text-slate-500 leading-relaxed">ورود امن به پنل آنالیز و برآورد پروژه</p>
            <div className="mt-4 flex items-center justify-center gap-2 text-xs text-emerald-900/75">
              <Sparkles size={14} className="shrink-0 text-emerald-600" />
              <span>فهرست بها، آنالیز ردیف و گزارش مالی</span>
            </div>
          </div>

          {error && (
            <div className="relative mb-4 rounded-xl border border-red-200/80 bg-red-50/90 px-3 py-2.5 text-center text-sm text-red-800">
              {error}
            </div>
          )}
          {reason === '1' && (
            <div className="relative mb-4 rounded-xl border border-amber-200/80 bg-amber-50/90 px-3 py-2.5 text-center text-sm text-amber-800">
              به‌دلیل عدم فعالیت، نشست شما منقضی شد. لطفاً دوباره وارد شوید.
            </div>
          )}

          <form onSubmit={handleSubmit} className="relative space-y-4">
            <div>
              <label htmlFor="username" className="mb-1 block text-sm font-medium text-slate-700">
                نام کاربری
              </label>
              <input
                id="username"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                disabled={loading}
                className="w-full rounded-xl border border-slate-200/90 bg-white/85 px-4 py-2.5 text-slate-800 shadow-sm transition focus:border-emerald-600 focus:outline-none focus:ring-2 focus:ring-emerald-600/25 disabled:bg-slate-100"
                placeholder="نام کاربری"
                autoComplete="username"
              />
            </div>
            <div>
              <label htmlFor="password" className="mb-1 block text-sm font-medium text-slate-700">
                رمز عبور
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={loading}
                className="w-full rounded-xl border border-slate-200/90 bg-white/85 px-4 py-2.5 text-slate-800 shadow-sm transition focus:border-emerald-600 focus:outline-none focus:ring-2 focus:ring-emerald-600/25 disabled:bg-slate-100"
                placeholder="رمز عبور"
                autoComplete="current-password"
              />
            </div>
            <div className="flex items-center">
              <input
                id="remember"
                type="checkbox"
                checked={remember}
                onChange={(e) => setRemember(e.target.checked)}
                disabled={loading}
                className="h-4 w-4 rounded border-slate-300 text-emerald-700 focus:ring-emerald-600"
              />
              <label htmlFor="remember" className="mr-2 text-sm text-slate-600">
                مرا به خاطر بسپار
              </label>
            </div>

            <button
              type="submit"
              disabled={loading || !username || !password}
              className="group relative w-full overflow-hidden rounded-xl bg-gradient-to-l from-emerald-800 to-[#0a4d29] py-2.5 font-medium text-white shadow-md transition hover:from-emerald-900 hover:to-emerald-950 focus:outline-none focus:ring-2 focus:ring-emerald-600/45 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <span
                className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/20 to-transparent transition-transform duration-700 group-hover:translate-x-full"
                aria-hidden
              />
              <span className="relative inline-flex w-full items-center justify-center gap-2">
                {loading ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin" />
                    در حال ورود...
                  </>
                ) : (
                  <>
                    <LogIn className="h-5 w-5" />
                    ورود
                  </>
                )}
              </span>
            </button>

            <button
              type="button"
              onClick={() => navigate('/login-otp', { state: { from } })}
              className="w-full rounded-xl border border-emerald-200 bg-emerald-50/80 py-2.5 text-sm font-medium text-emerald-900 transition hover:bg-emerald-100/90"
            >
              ورود با شماره موبایل (کد پیامکی)
            </button>
          </form>
        </div>
      </div>

      {secondStepOpen && (
        <div className="fixed inset-0 z-[220] flex items-center justify-center bg-black/40 p-4" dir="rtl">
          <div className="w-full max-w-md rounded-2xl border border-emerald-200/80 bg-white p-5 shadow-2xl">
            <div className="mb-4 text-center">
              <div className="mx-auto inline-flex h-11 w-11 items-center justify-center rounded-xl bg-emerald-100 text-emerald-800">
                <ShieldCheck size={20} />
              </div>
              <h3 className="mt-2 text-base font-bold text-slate-800">تایید مرحله دوم ورود</h3>
              <p className="mt-1 text-xs text-slate-600">کد ۵ رقمی پیامک‌شده را وارد کنید.</p>
            </div>
            {otpError && (
              <div className="mb-3 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">{otpError}</div>
            )}
            <div className="grid grid-cols-5 gap-2 mb-3" dir="ltr">
              {codeDigits.map((digit, idx) => (
                <input
                  key={idx}
                  ref={(el) => {
                    inputRefs.current[idx] = el;
                  }}
                  value={digit}
                  onChange={(e) => onDigitChange(idx, e.target.value)}
                  className="h-11 rounded-lg border border-slate-300 text-center text-lg font-bold outline-none focus:border-emerald-600 focus:ring-2 focus:ring-emerald-600/20"
                  maxLength={1}
                  inputMode="numeric"
                />
              ))}
            </div>
            <div className="flex items-center justify-between gap-2">
              <button
                type="button"
                onClick={() => requestSecondStepOtp()}
                disabled={otpLoading || cooldownLeft > 0}
                className="text-sm text-emerald-700 disabled:text-slate-400"
              >
                {cooldownLeft > 0 ? `ارسال مجدد تا ${cooldownLeft} ثانیه` : 'ارسال مجدد کد'}
              </button>
              <button
                type="button"
                onClick={verifySecondStep}
                disabled={otpLoading || code.length !== 5}
                className="inline-flex items-center gap-2 rounded-lg bg-[#0a4d29] px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
              >
                {otpLoading ? <Loader2 size={15} className="animate-spin" /> : null}
                تایید و ورود
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Login;
